import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import i18n from "@/lib/i18n";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const stripNonDigits = (value: string | number): string => {
  return value.toString().replace(/\D/g, "");
};

export const formatWithSpaces = (
  digitsOnly: string | number,
  options?: { separator?: string }
): string => {
  const { separator = " " } = options || {};
  return stripNonDigits(digitsOnly).replace(
    /(\d)(?=(\d{3})+(?!\d))/g,
    "$1" + separator
  );
};

export const calculateProgressPercentage = (
  targetAmount: string,
  gainedAmount: string
) => {
  const target = parseFloat(targetAmount);
  const gained = parseFloat(gainedAmount);

  if (isNaN(target) || isNaN(gained) || target === 0) {
    return 0;
  }

  return Math.min((gained / target) * 100, 100);
};

export const formatNumber = (value: number | string) => {
  if (!value) return "0";
  return parseFloat(value.toString())
    .toLocaleString("FR-fr", { minimumFractionDigits: 0 })
    .replace(/[\u200B\u00A0\u202F]/g, " ")
    .replace(",", ".");
};

// sessionStorage flag read by DailyCheckIn to auto-open the check-in dialog
// once the user lands on home via the `startapp=checkin` deep link (bot
// reminder button). Cleared as soon as it's consumed.
export const AUTO_OPEN_CHECKIN_KEY = "auto_open_checkin";

export function parseStartAppString(
  startApp: string | undefined
): Record<string, string> | undefined {
  if (!startApp) {
    return undefined;
  }

  // Simple flag param (no key_value pairing), e.g. startapp=checkin — the
  // deep link sent from the daily check-in reminder Telegram message.
  if (startApp === "checkin") {
    return { checkin: "true" };
  }

  const regex = /([a-zA-Z]+)_(.*?)__/g;
  let match: RegExpExecArray | null;
  const result: Record<string, string> = {};

  while ((match = regex.exec(startApp)) !== null) {
    const key = match[1];
    const value = match[2];
    result[key] = value;
  }

  const lastPart = startApp.split("__").pop();
  const lastKeyMatch = lastPart ? /([a-zA-Z]+)_(.*)/.exec(lastPart) : null;
  if (lastKeyMatch) {
    result[lastKeyMatch[1]] = lastKeyMatch[2];
  }

  return result;
}

export function getFullName(data: { first_name: string; last_name: string }) {
  return (data.first_name && data.first_name + " ") + data.last_name;
}

export function getMonthNames(): string[] {
  const formatter = new Intl.DateTimeFormat(i18n.language, {
    month: "long",
  });

  return Array.from({ length: 12 }, (_, index) => {
    const date = new Date(Date.UTC(2024, index, 1));
    return formatter.format(date);
  });
}

export function getUserDeviceType() {
  const userAgent = navigator.userAgent.toLowerCase();

  let deviceType = "unknown";
  if (userAgent.includes("android")) {
    deviceType = "android";
  } else if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
    deviceType = "ios";
  } else if (userAgent.includes("macintosh") || userAgent.includes("macos")) {
    deviceType = "macos";
  } else if (userAgent.includes("windows")) {
    deviceType = "windows";
  }
  return deviceType;
}
